import { useState, useEffect } from 'react';
import axios from 'axios';
import useLang from '../../hooks/useLang';
import { t, getLang } from '../../utils/i18n';

const api = axios.create({ baseURL: '/api', timeout: 60000 });

const scoreColor = (score) =>
  score >= 7 ? 'var(--green)' : score >= 4 ? 'var(--yellow)' : 'var(--red)';

const moatColor = (rating) => {
  if (!rating) return 'var(--text-muted)';
  const r = rating.toLowerCase();
  if (r.includes('wide') || r.includes('宽') || r.includes('lebar')) return 'var(--green)';
  if (r.includes('narrow') || r.includes('窄') || r.includes('sempit')) return 'var(--yellow)';
  return 'var(--red)';
};

function ScoreBar({ score, max = 10 }) {
  const color = scoreColor(score);
  const pct = Math.max(0, Math.min(100, (score / max) * 100));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0 10px' }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

export default function MoatTab({ symbol }) {
  const { lang } = useLang();
  const [moat, setMoat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    setMoat(null);
    setError(null);
    api.get(`/ai/moat/${symbol}`)
      .then(r => { if (r.data) setMoat(r.data); })
      .catch(() => {});
  }, [symbol]);

  const generate = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post('/ai/moat', { symbol, force, lang: getLang() });
      setMoat(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate moat analysis.');
    } finally { setLoading(false); }
  };

  const mColor = moatColor(moat?.moatRating);

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>
          {symbol} · {t('moat', lang)}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {moat && (
            <button className="btn btn-ghost" onClick={() => generate(true)} disabled={loading} style={{ fontSize: 12 }}>
              {t('regenerateMoat', lang)}
            </button>
          )}
          {!moat && !loading && (
            <button className="btn btn-primary" onClick={() => generate(false)} disabled={loading}>
              {t('generateMoat', lang)}
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {!moat && !loading && !error && (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)', fontSize: 14 }}>
          {t('noMoatData', lang)}
        </div>
      )}

      {loading && (
        <div className="card" style={{ padding: '40px 24px' }}>
          {[80, 60, 90, 50, 70].map((w, i) => (
            <div key={i}>
              <div className="skeleton" style={{ height: 16, width: `${w}%`, marginBottom: 8, borderRadius: 4 }} />
              <div className="skeleton" style={{ height: 6, width: '100%', marginBottom: 16, borderRadius: 3 }} />
            </div>
          ))}
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
            {t('moatAnalysing', lang)}
          </div>
        </div>
      )}

      {moat && !loading && (
        <div>
          {/* Overall moat card */}
          <div className="card" style={{ borderLeft: `4px solid ${mColor}`, paddingLeft: 20 }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: mColor, marginBottom: 4 }}>
              {moat.moatRating}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: mColor, marginBottom: 2 }}>
              {t('overallScore', lang)}: {moat.overallScore} / 10
            </div>
            <ScoreBar score={moat.overallScore} />
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
              {moat.summary}
            </p>
          </div>

          {/* Criteria */}
          {(moat.criteria || []).map((c, i) => {
            const cColor = scoreColor(c.score);
            return (
              <div key={i} className="card">
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                    {i + 1}. {c.name}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: cColor, fontWeight: 600, marginBottom: 2 }}>
                  Score: {c.score} / 10
                </div>
                <ScoreBar score={c.score} />
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                  {c.explanation}
                </p>
              </div>
            );
          })}

          {/* Meta */}
          {moat._meta && (
            <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
              Generated {moat._meta.ageHours}h ago
            </div>
          )}
        </div>
      )}
    </div>
  );
}
